function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _objectSpreadProps(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
var __generator = this && this.__generator || function(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return(g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g);
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
};
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import { getEvent, getId as getEventId } from "../state/event";
import { checkPortal } from "../state/system";
import { useConfigKeysQuery, useConfigQuery, useConfigUpdateMutation } from "../services/config/queries";
import { isFFEnabled } from "../state/flags";
import { oldUpdate } from "../services/config/api";
import { useQueryClient } from "@tanstack/react-query";
var ConfigStatus;
(function(ConfigStatus) {
    ConfigStatus["Enabled"] = "1";
    ConfigStatus["Disabled"] = "0";
})(ConfigStatus || (ConfigStatus = {}));
// VALID_NULLABLE_CONFIG_KEYS is the list of configuration keys that can have a null
// as valid value.
// These configurations should not be considered as configs with missing values and
// be replaced with default value if the value is null.
//
// One good example is `event_max_duration_days` which will have a null value if the
// event does not have a max duration set; otherwise it will have a number value.
var VALID_NULLABLE_CONFIG_KEYS = [
    "event_max_duration_days",
    "reg_open_period"
];
var useConfig = function() {
    var eventId = useSelector(getEventId);
    var timezone = useSelector(getEvent).timezone;
    var isExperiencePortal = useSelector(checkPortal).isExperiencePortal;
    var isNewConfigUapiEnabled = useSelector(function(state) {
        return isFFEnabled(state, "new_config_uapi");
    });
    var queryClient = useQueryClient();
    var keysQuery = useConfigKeysQuery([
        isNewConfigUapiEnabled
    ], {
        enabled: isExperiencePortal
    });
    var configQuery = useConfigQuery([
        eventId,
        isNewConfigUapiEnabled
    ], {
        enabled: !!eventId
    });
    var _useConfigUpdateMutation = useConfigUpdateMutation(), isUpdatingConfig = _useConfigUpdateMutation.isLoading, updateConfig = _useConfigUpdateMutation.mutateAsync;
    // react-query return isLoading as true when the enabled flag is false
    // so in the case of CompanyPortal the keysQuery.isLoading will always return true
    // hence added one more check - !isExperiencePortal
    var loading = isUpdatingConfig || keysQuery.isLoading && isExperiencePortal || configQuery.isLoading;
    var keys = keysQuery.data;
    var conf = configQuery.data;
    var merged = useMemo(function() {
        var obj = (conf !== null && conf !== void 0 ? conf : []).reduce(function(acc, cur) {
            if (isNewConfigUapiEnabled) {
                acc[cur.keyId] = {
                    config: cur,
                    key: keys === null || keys === void 0 ? void 0 : keys.find(function(k) {
                        return k.id === cur.keyId;
                    })
                };
            } else {
                acc[cur.key] = {
                    config: cur,
                    key: keys === null || keys === void 0 ? void 0 : keys.find(function(k) {
                        return k.key === cur.key;
                    })
                };
            }
            return acc;
        }, {});
        return Object.freeze(obj);
    }, [
        conf,
        isNewConfigUapiEnabled,
        keys
    ]);
    // gets the value of a record from the configuration array stored in the state.
    // in case of key not being existent in the configuration object, it looks up
    // in the keys store and returns the default value (returns the first one when
    // there are multiple default values), if it doesn't exist in the default keys
    // array either then it return null
    var get = useCallback(function(key) {
        var _targetKey_default;
        var target = merged[key];
        if (!(target === null || target === void 0 ? void 0 : target.config)) {
            return null;
        }
        var targetConfig = target.config, targetKey = target.key;
        // some config keys may have null as valid value; if this key is among those
        // and the value is null, return null - and do not use the default value
        if (VALID_NULLABLE_CONFIG_KEYS.includes(key) && (targetConfig === null || targetConfig === void 0 ? void 0 : targetConfig.value) === null) {
            return null;
        }
        if ((targetKey === null || targetKey === void 0 ? void 0 : targetKey.type) === "datetime_range" && (targetConfig === null || targetConfig === void 0 ? void 0 : targetConfig.value)) {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            var _JSON_parse = JSON.parse(targetConfig.value), start_datetime = _JSON_parse.start_datetime, end_datetime = _JSON_parse.end_datetime;
            return [
                moment.tz(start_datetime, timezone),
                moment.tz(end_datetime, timezone)
            ];
        }
        // if value property exists in the target returns that, other wise look for the
        // default property, if that exists then splits the value of default using
        // `,` character so that it will return the first value in case there are multiple
        // default values; in rare scenario of neither value nor default property being
        // defined, return null
        var confValue = targetConfig === null || targetConfig === void 0 ? void 0 : targetConfig.value;
        var keyValue = targetKey === null || targetKey === void 0 ? void 0 : (_targetKey_default = targetKey.default) === null || _targetKey_default === void 0 ? void 0 : _targetKey_default.split(",")[0];
        if (confValue !== undefined) {
            return confValue;
        }
        if (keyValue !== undefined) {
            return keyValue;
        }
        return null;
    }, [
        merged,
        timezone
    ]);
    var find = useCallback(function(key) {
        var _merged_key;
        return (_merged_key = merged[key]) === null || _merged_key === void 0 ? void 0 : _merged_key.config;
    }, [
        merged
    ]);
    // asynchronous function to either update a configuration object or create a new one
    // if it does not exist; returns the newly created/update record
    var set = useCallback(function() {
        var _ref = _asyncToGenerator(function(configs) {
            var response, error;
            return __generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        _state.trys.push([
                            0,
                            4,
                            ,
                            5
                        ]);
                        configs.forEach(function(config) {
                            var _merged_config_keyId;
                            var target = (_merged_config_keyId = merged[config.keyId]) === null || _merged_config_keyId === void 0 ? void 0 : _merged_config_keyId.config;
                            if (typeof target === "undefined") {
                                throw new Error("invalid configuration key ".concat(config.keyId));
                            }
                        });
                        if (!isNewConfigUapiEnabled) return [
                            3,
                            2
                        ];
                        return [
                            4,
                            updateConfig({
                                eventId: eventId,
                                configs: configs
                            })
                        ];
                    case 1:
                        _state.sent();
                        return [
                            2
                        ];
                    case 2:
                        response = configs.map(function(param) {
                            var keyId = param.keyId, value = param.value;
                            var _merged_keyId;
                            var target = (_merged_keyId = merged[keyId]) === null || _merged_keyId === void 0 ? void 0 : _merged_keyId.config;
                            return oldUpdate(eventId, target.id, _objectSpreadProps(_objectSpread({}, target), {
                                value: value
                            }));
                        });
                        return [
                            4,
                            Promise.all(response)
                        ];
                    case 3:
                        _state.sent();
                        queryClient.invalidateQueries({
                            queryKey: [
                                "config"
                            ]
                        });
                        return [
                            3,
                            5
                        ];
                    case 4:
                        error = _state.sent();
                        throw new Error("failed to update configuration: ".concat(error));
                    case 5:
                        return [
                            2
                        ];
                }
            });
        });
        return function(configs) {
            return _ref.apply(this, arguments);
        };
    }(), [
        isNewConfigUapiEnabled,
        merged,
        updateConfig,
        eventId,
        queryClient
    ]);
    /**
   * @description checks if the provided configuration keys are enabled
   * @param {String} getKeys - configuration key to check
   * @param {String, String, String} getKeys - n number of configuration keys to check
   * @returns {boolean} true if and only if all the config keys passed are enabled.
   * return `true` if it exists AND its value is set to `1`
   */ var isConfigEnabled = useCallback(function() {
        for(var _len = arguments.length, allKeys = new Array(_len), _key = 0; _key < _len; _key++){
            allKeys[_key] = arguments[_key];
        }
        var values = allKeys.map(get);
        return values === null || values === void 0 ? void 0 : values.some(function(value) {
            return value !== null && value === ConfigStatus.Enabled;
        });
    }, [
        get
    ]);
    // returns all enabled configuration settings
    var enabled = useCallback(function() {
        return conf.filter(function(c) {
            return (c === null || c === void 0 ? void 0 : c.value) === ConfigStatus.Enabled;
        });
    }, [
        conf
    ]);
    return {
        get: get,
        find: find,
        set: set,
        isConfigEnabled: isConfigEnabled,
        enabled: enabled,
        // TODO: EXP-21528 - remove loading and use isUpdating and isFetching separately
        loading: loading,
        isUpdating: isUpdatingConfig,
        isFetching: keysQuery.isLoading && isExperiencePortal || configQuery.isLoading
    };
};
export default useConfig;
